import type { Reminder, UpdateReminderBody } from '@kanto/shared';
import { useEffect, useRef, useState, type ReactElement, type TouchEvent } from 'react';
import { dayOf, formatRemindAt, fromLocalInput, isPast, isToday, toLocalInput } from '../lib/dates';
import { useT } from '../lib/i18n';
import { TagChips } from './TagChips';

/**
 * One reminder: a box to tick, what it says, and when it is for.
 *
 * Deliberately the same row as `TodoRow` — the same hidden actions, the same
 * swipe, the same title that is an `<input>` at all times so a click keeps the
 * caret where it landed. Two lists that behave differently for no reason are two
 * things to learn.
 *
 * The moment is the one difference on screen, and it carries the tone: `ink-400`
 * ahead, the accent today, `alert` once it has passed. Past is a **minute** and
 * not a day, which is the difference from a due date: a reminder for 14:30 is
 * late at 14:31, not at midnight.
 */

/** How far the row slides to uncover its actions, in pixels. */
const REVEAL = 96;

export function ReminderRow({
  reminder,
  onUpdate,
  onArchive,
  highlighted = false,
}: {
  reminder: Reminder;
  onUpdate: (patch: UpdateReminderBody) => void;
  onArchive: () => void;
  /** Named by a search hit, exactly as on a todo row. */
  highlighted?: boolean;
}): ReactElement {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const swipe = useRef<{ x: number; y: number; horizontal: boolean } | null>(null);
  const row = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (highlighted) row.current?.scrollIntoView({ block: 'center' });
  }, [highlighted]);

  const done = reminder.doneAt !== null;

  /**
   * The draft title lives in the DOM rather than in state, so there is nothing to
   * keep in step with the server: `key={reminder.title}` remounts the field when
   * a different title arrives — from another device, or from a rollback — and
   * while somebody is typing that value has not changed, so nothing moves under
   * them.
   */
  const commitTitle = (input: HTMLInputElement): void => {
    const next = input.value.trim();
    // Nothing typed, or nothing changed: an empty title is refused by the server,
    // and the row would look blank until the refusal arrived.
    if (!next || next === reminder.title) {
      input.value = reminder.title;
      return;
    }
    onUpdate({ title: next });
  };

  /**
   * A horizontal drag reveals the actions; a vertical one is the page scrolling
   * and must be left alone. The direction is decided once, on the first few
   * pixels, because a gesture that changes its mind halfway is one that scrolls
   * the page sideways by accident.
   */
  const onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (touch) swipe.current = { x: touch.clientX, y: touch.clientY, horizontal: false };
  };

  const onTouchMove = (event: TouchEvent): void => {
    const start = swipe.current;
    const touch = event.touches[0];
    if (!start || !touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (!start.horizontal && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
      start.horizontal = true;
    }
    if (start.horizontal) setRevealed(dx < -12);
  };

  const onTouchEnd = (): void => {
    swipe.current = null;
  };

  // A reminder that has been dealt with is no longer late, whenever it was for:
  // the alarming colour is for what still needs attention.
  const tone = done
    ? 'text-ink-400'
    : isPast(reminder.remindAt)
      ? 'text-alert'
      : isToday(dayOf(reminder.remindAt))
        ? 'text-accent'
        : 'text-ink-400';

  const reschedule = (local: string): void => {
    const moment = fromLocalInput(local);
    if (moment) onUpdate({ remindAt: moment });
  };

  return (
    <li ref={row} className="group relative overflow-hidden">
      {/* Underneath the row, uncovered by the swipe. Hidden from assistive
          technology and from the keyboard: the same actions sit in the row
          itself above, and offering each of them twice would double the length
          of the list to anybody using either. */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 lg:hidden" aria-hidden>
        <button
          type="button"
          tabIndex={-1}
          onClick={onArchive}
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-alert"
        >
          <ArchiveIcon />
        </button>
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: revealed ? `translateX(-${REVEAL}px)` : undefined }}
        // `touch-action: pan-y` hands vertical scrolling back to the browser, so
        // the handlers above never have to fight it — the one part of a swipe
        // that cannot be got right in JavaScript.
        // `relative` so the overlay below travels with the row when it is swiped
        // aside, rather than staying anchored to the list item underneath.
        className={`relative flex touch-pan-y items-start gap-3 px-4 py-3 transition-transform duration-200 ${
          highlighted ? 'bg-accent-soft' : 'bg-surface'
        }`}
      >
        <input
          type="checkbox"
          checked={done}
          onChange={(event) => onUpdate({ done: event.target.checked })}
          aria-label={t(done ? 'reminders.markStanding' : 'reminders.markDone', reminder.title)}
          className="mt-1 size-5 shrink-0 accent-accent"
        />

        <div className="min-w-0 flex-1">
          <input
            type="text"
            key={reminder.title}
            defaultValue={reminder.title}
            onBlur={(event) => commitTitle(event.currentTarget)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
              if (event.key === 'Escape') {
                event.currentTarget.value = reminder.title;
                event.currentTarget.blur();
              }
            }}
            aria-label={t('reminders.rename', reminder.title)}
            className={`w-full bg-transparent text-base outline-none ${
              done ? 'text-ink-400 line-through' : 'text-ink-900'
            }`}
          />
          <TagChips tags={reminder.tags} />
        </div>

        {/* Always shown, unlike a todo's due date, because a reminder without one
            is not a reminder. `tabular-nums` so a column of times lines up. */}
        <span className={`mt-0.5 shrink-0 text-sm tabular-nums ${tone}`}>
          {formatRemindAt(reminder.remindAt, t)}
        </span>

        {/* The actions on a pointer, laid over the right of the row rather than
            beside it: reserved inline, their width would be a hand's breadth of
            empty space in every row of a list that is mostly one line long. They
            appear on `focus-within` as well as on hover, so a keyboard reaches
            them without a mouse ever moving. */}
        <div className="absolute inset-y-0 right-0 hidden items-center gap-1 bg-surface pr-3 pl-6 group-focus-within:flex group-hover:flex">
          <label className="sr-only" htmlFor={`remind-${reminder.id}`}>
            {t('reminders.at')}
          </label>
          <input
            id={`remind-${reminder.id}`}
            type="datetime-local"
            value={toLocalInput(reminder.remindAt)}
            onChange={(event) => reschedule(event.target.value)}
            className="min-h-9 rounded-lg border border-line bg-surface px-2 text-xs text-ink-500"
          />
          <button
            type="button"
            onClick={onArchive}
            aria-label={t('reminders.archive', reminder.title)}
            className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 transition-colors hover:bg-alert-soft hover:text-alert"
          >
            <ArchiveIcon />
          </button>
        </div>
      </div>
    </li>
  );
}

function ArchiveIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4" />
    </svg>
  );
}
